import type {
  BlackbookDetailsResponse,
  BlackbookPreferencesRequestBody,
} from '@core/Areas/Blackbook/types'
import { useMutation } from '@tanstack/react-query'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { postBlackbookAlertPreferences } from '@core/Areas/Blackbook/helpers/api'

/**
 * Optimistically update the Blackbook Details cache with updated SMS or Email alert preferences, before performing the update
 * on the server. Invalidate the cache when done, so useQuery hooks don't show stale data.
 */
export const useBlackbookSmsEmailAlertMutation = () =>
  useMutation({
    mutationFn: (newPreferences: BlackbookPreferencesRequestBody) =>
      postBlackbookAlertPreferences(newPreferences),
    onMutate: async (newPreferences: BlackbookPreferencesRequestBody) => {
      await queryClient.cancelQueries({ queryKey: [queryKeys.blackbookDetails] })

      const previousPreferences = queryClient.getQueryData([queryKeys.blackbookDetails])

      // Optimistically update the cache
      queryClient.setQueryData<BlackbookDetailsResponse>([queryKeys.blackbookDetails], oldData => {
        return {
          Entries: oldData?.Entries ?? null,
          SendEmail: newPreferences.SendEmail,
          SendSms: newPreferences.SendSms,
        }
      })

      return { previousPreferences }
    },
    onError: (error, variables, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData([queryKeys.blackbookDetails], context.previousPreferences)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.blackbookDetails] })
    },
  })
